import { ApiUtil } from 'reactifi';
import { sanitizeUrl } from '@braintree/sanitize-url';
import i18n from 'lib/utility/i18n';

const MORE_TO_EXPLORE_MAX = 2;

// Both slots must correlate to the completed module; the random backfill is for the dashboard widget only
const CORRELATED_MATCH_TYPES = 'subtopic|topic';

export const NEXT_STEPS_PARAM = 'next_steps';

export const completedContentId = () => new URLSearchParams(window.location.search).get(NEXT_STEPS_PARAM);

const isCompleted = content =>
  content?.user_content?.progress?.status === 'completed' || content?.user_content?.state === 'completed';

const contentUrl = (currentUriContext, contentId, extraParams = '') =>
  sanitizeUrl(`${currentUriContext}/${contentId}${extraParams}`);

const fetchJsonApi = async url => {
  const json = await ApiUtil.loadData(url, { headers: { Accept: 'application/vnd.api+json' } }, false);

  return (json?.data || []).map(record => ({ id: record.id, ...record.attributes }));
};

const moduleItem = (content, startUrl) => ({
  id: content.id,
  type: 'module',
  title: content.name,
  description: content.description,
  duration: content.duration,
  imageUrl: content.image_url,
  startUrl,
});

export const upNextFromPlaylist = (playlist, contentId, currentUriContext) => {
  const items = playlist?.content || [];
  const completedIndex = items.findIndex(content => String(content.id) === String(contentId));

  if (completedIndex === -1) return { completedModule: null, upNext: null };

  const next = items.slice(completedIndex + 1).find(content => !isCompleted(content));

  return {
    completedModule: items[completedIndex],
    upNext: next ? moduleItem(next, contentUrl(currentUriContext, next.id)) : null,
  };
};

const fetchRecommendations = async ({ programId, programSlug, contentId, categories, limit }) => {
  const recommendations = await fetchJsonApi(
    `/api/data/user_recommendations?program_id=${programId}` +
      `&filter[categories]=${encodeURIComponent(categories)}` +
      `&filter[match_types]=${encodeURIComponent(CORRELATED_MATCH_TYPES)}` +
      `&reference_content_id=${encodeURIComponent(contentId)}&limit=${limit}`
  );

  return recommendations.map(rec => {
    const params = new URLSearchParams({ recommended: 'yes', match_type: rec.match_type });
    const url = sanitizeUrl(
      `/student/dashboard/${programSlug}/${rec.playlist_slug}/${rec.content_id}?${params.toString()}`
    );

    return moduleItem(rec, url);
  });
};

// Slot 1 is the tool correlated to the completed module; slot 2 is the most correlated
// module/article/calculator, which also backfills slot 1 when no correlated tool exists.
// Uncorrelated content is never surfaced, so the panel can end up with one card or none.
export const loadMoreToExplore = async ({ programId, programSlug, contentId }) => {
  if (!programId || !contentId) return [];

  const [tools, educational] = await Promise.all([
    fetchRecommendations({ programId, programSlug, contentId, categories: 'tool', limit: 1 }).catch(() => []),
    fetchRecommendations({
      programId,
      programSlug,
      contentId,
      categories: 'lesson|calculator|article',
      limit: MORE_TO_EXPLORE_MAX,
    }).catch(() => []),
  ]);

  return [...tools, ...educational].slice(0, MORE_TO_EXPLORE_MAX);
};

export const withPanelReturnUrl = authUrls => {
  const returnUrl = `${window.location.pathname}${window.location.search}`;

  return (authUrls || []).map(entry => ({
    ...entry,
    url: `${entry.url}${entry.url.includes('?') ? '&' : '?'}original_url=${encodeURIComponent(returnUrl)}`,
  }));
};

export const dashboardUrlWithoutPanel = () => {
  const params = new URLSearchParams(window.location.search);
  params.delete(NEXT_STEPS_PARAM);
  const query = params.toString();

  return sanitizeUrl(`${window.location.pathname}${query ? `?${query}` : ''}`);
};

export const nextStepsTitle = (completedModule, playlist) =>
  playlist?.allCompleted ? playlist.name : completedModule?.name || i18n.t('this module');
